import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Image, Upload, Wand2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const btn = "border border-slate-200 bg-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-40";

export const MerchantTools = () => {
  const qc = useQueryClient();
  const file = useRef(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); qc.invalidateQueries({ queryKey: ["a-merchants"] }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const backfill = () => run(async () => {
    const r = await api.post("/admin/merchants/backfill");
    toast.success(`${r.data.updated} store${r.data.updated === 1 ? "" : "s"} updated from coupon links`, { duration: 9000 });
  });
  const logo = useQuery({ queryKey: ["logo-fill"], queryFn: () => api.get("/admin/merchants/fill-logos").then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.running ? 3000 : false) });
  const prevRunning = useRef(false);
  useEffect(() => {
    const d = logo.data;
    if (prevRunning.current && d && !d.running) {
      qc.invalidateQueries({ queryKey: ["a-merchants"] });
      d.error ? toast.error(`Logo fill failed: ${d.error}`) : toast.success(`Logos: ${d.filled} filled out of ${d.checked} stores checked`, { duration: 9000 });
    }
    prevRunning.current = !!d?.running;
  }, [logo.data, qc]);
  const fillLogos = () => run(async () => { await api.post("/admin/merchants/fill-logos"); await logo.refetch(); });
  const exportCsv = () => run(async () => {
    const r = await api.get("/admin/merchants/export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "bachatraja-stores.csv";
    a.click();
  });
  const importCsv = (f) => run(async () => {
    const fd = new FormData();
    fd.append("file", f);
    const r = await api.post("/admin/merchants/import-csv", fd);
    toast.success(`Stores: ${r.data.created} new, ${r.data.updated} updated, ${r.data.unchanged} unchanged, ${r.data.skipped} skipped`, { duration: 9000 });
  });
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="merchant-tools">
      <button disabled={busy} onClick={backfill} className={btn} data-testid="merchants-backfill-button"><Wand2 size={15} /> Backfill stores from coupons</button>
      <button disabled={busy || logo.data?.running} onClick={fillLogos} className={btn} data-testid="merchants-fill-logos-button">
        <Image size={15} /> {logo.data?.running ? "Filling logos…" : "Fill store logos"}
      </button>
      <button disabled={busy} onClick={exportCsv} className={btn} data-testid="merchants-export-button"><Download size={15} /> Export CSV</button>
      <button disabled={busy} onClick={() => file.current?.click()} className={btn} data-testid="merchants-import-button"><Upload size={15} /> Import CSV</button>
      <input ref={file} type="file" accept=".csv,text/csv" className="hidden" data-testid="merchants-import-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) importCsv(f); }} />
      {logo.data?.finished_at && !logo.data.running && <span className="text-xs text-slate-500" data-testid="logo-fill-status">Last logo fill: {logo.data.filled} of {logo.data.checked} filled</span>}
      <span className="text-xs text-slate-500 basis-full">CSV columns: slug, name, website, affiliate_url, logo_url, description. Blank cells keep the current value.</span>
    </div>
  );
};
